import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../../config';
import AutomationModal from '../../Modals/AutomationModal/AutomationModal';
import AutomationHistoryModal from '../../Modals/AutomationHistoryModal/AutomationHistoryModal';
import './AutomationTab.css';

function AutomationTab({ pendingEditId, onEditComplete }) {
    const [schedules, setSchedules] = useState([]);
    const [channels, setChannels] = useState([]);
    const [tags, setTags] = useState([]);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedAutomationForHistory, setSelectedAutomationForHistory] = useState(null);

    const [youtubeChannels, setYoutubeChannels] = useState([]);
    const [xAccounts, setXAccounts] = useState([]);
    const [tiktokAccounts, setTiktokAccounts] = useState([]);
    const [instagramAccounts, setInstagramAccounts] = useState([]);

    const [showNotificationSettings, setShowNotificationSettings] = useState(false);
    const [notificationSettings, setNotificationSettings] = useState({
        notification_channel_id: null,
        notify_on_last_content: true,
        notify_on_empty: true
    });
    const [notificationSaving, setNotificationSaving] = useState(false);

    // Filtre state'leri
    const [activePlatformFilters, setActivePlatformFilters] = useState([]);
    const [activeTagFilters, setActiveTagFilters] = useState([]);
    const [showTagFilterDropdown, setShowTagFilterDropdown] = useState(false);

    useEffect(() => {
        const loadAllData = async () => {
            await Promise.all([
                fetchSchedules(),
                fetchChannels(),
                fetchTags(),
                fetchYouTubeChannels(),
                fetchXAccounts(),
                fetchTikTokAccounts(),
                fetchInstagramAccounts()
            ]);
        };
        loadAllData();
    }, []);

    useEffect(() => {
        fetchNotificationSettings();
    }, []);

    // Tag dropdown dışına tıklandığında kapat
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showTagFilterDropdown && !e.target.closest('.tag-filter-wrapper')) {
                setShowTagFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTagFilterDropdown]);

    const fetchNotificationSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/automation/notification-settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.success) {
                setNotificationSettings(response.data.settings);
            }
        } catch (error) {
            console.error('Bildirim ayarları yüklenemedi:', error);
        }
    };

    const saveNotificationSettings = async () => {
        try {
            setNotificationSaving(true);
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/automation/notification-settings`, notificationSettings, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setShowNotificationSettings(false);
        } catch (error) {
            console.error('Bildirim ayarları kaydedilemedi:', error);
        } finally {
            setNotificationSaving(false);
        }
    };

    const sendTestNotification = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/automation/notification-settings/test`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.success) {
                alert('Test bildirimi gönderildi!');
            }
        } catch (error) {
            alert('Test bildirimi gönderilemedi: ' + (error.response?.data?.error || error.message));
        }
    };

    useEffect(() => {
        if (pendingEditId && schedules.length > 0) {
            const searchId = typeof pendingEditId === 'string' ? parseInt(pendingEditId) : pendingEditId;
            const automationToEdit = schedules.find(s => s.id === searchId);

            if (automationToEdit) {
                setTimeout(() => {
                    openModal(automationToEdit);
                    if (onEditComplete) {
                        onEditComplete();
                    }
                }, 100);
            }
        }
    }, [pendingEditId, schedules]);

    const fetchSchedules = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/automation`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success || response.data.rules) {
                setSchedules(response.data.rules || []);
            }
        } catch (error) {
            console.error('Zamanlamalar yüklenemedi:', error);
            setSchedules([]);
        }
    };

    const fetchChannels = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/channels`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            let allChannels = response.data.channels || [];

            try {
                const xResponse = await axios.get(`${API_URL}/platforms/twitter/accounts`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (xResponse.data.accounts) {
                    const xChannels = xResponse.data.accounts.map(account => ({
                        id: `x_${account._id || account.id}`,
                        name: `@${account.username}`,
                        platform: 'x',
                        chat_id: (account._id || account.id).toString(),
                        username: account.username,
                        profile_photo: account.profilePicture || account.profile_image_url,
                        member_count: 0,
                        is_x_account: true,
                        original_id: account._id || account.id
                    }));

                    allChannels = [...allChannels, ...xChannels];
                }
            } catch (xError) { }

            setChannels(allChannels);
        } catch (error) {
            console.error('Kanallar yüklenemedi:', error);
            setChannels([]);
        }
    };

    const fetchTags = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/tags`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data) {
                if (response.data.tags) {
                    setTags(response.data.tags);
                } else if (Array.isArray(response.data)) {
                    setTags(response.data);
                } else {
                    setTags([]);
                }
            } else {
                setTags([]);
            }
        } catch (error) {
            console.error('Etiketler yüklenemedi:', error);
            setTags([]);
        }
    };

    const fetchYouTubeChannels = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/youtube/channels`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setYoutubeChannels(response.data.channels || []);
        } catch (error) {
            setYoutubeChannels([]);
        }
    };

    const fetchXAccounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/platforms/twitter/accounts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const accounts = (response.data.accounts || []).map(acc => ({
                id: acc._id || acc.id,
                name: acc.displayName || acc.username || acc.name,
                username: acc.username,
                profile_image_url: acc.profilePicture || acc.profile_image_url,
                is_active: acc.isActive !== false,
                profileName: acc.profileName
            }));

            setXAccounts(accounts);
        } catch (error) {
            setXAccounts([]);
        }
    };

    const fetchTikTokAccounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/platforms/tiktok/accounts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setTiktokAccounts(response.data.accounts || []);
        } catch (error) {
            setTiktokAccounts([]);
        }
    };

    const fetchInstagramAccounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/platforms/instagram/accounts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setInstagramAccounts(response.data.accounts || []);
        } catch (error) {
            setInstagramAccounts([]);
        }
    };

    const toggleSchedule = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/automation/${id}`,
                { is_active: currentStatus ? 0 : 1 },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            await fetchSchedules();
        } catch (error) {
            console.error('Otomasyon durumu değiştirilemedi:', error);
        }
    };

    const openModal = (schedule = null) => {
        setEditingSchedule(schedule);
        setShowScheduleModal(true);
    };

    const closeModal = () => {
        setShowScheduleModal(false);
        setEditingSchedule(null);
        if (onEditComplete) {
            onEditComplete();
        }
    };

    const openHistoryModal = (schedule) => {
        setSelectedAutomationForHistory(schedule);
        setShowHistoryModal(true);
    };

    const closeHistoryModal = () => {
        setShowHistoryModal(false);
        setSelectedAutomationForHistory(null);
    };

    const handleModalSave = () => {
        fetchSchedules();
        if (onEditComplete) {
            onEditComplete();
        }
    };

    const handleModalDelete = () => {
        fetchSchedules();
        if (onEditComplete) {
            onEditComplete();
        }
    };

    // Platform filtre toggle
    const togglePlatformFilter = (platform) => {
        setActivePlatformFilters(prev =>
            prev.includes(platform)
                ? prev.filter(p => p !== platform)
                : [...prev, platform]
        );
    };

    // Tag filtre toggle
    const toggleTagFilter = (tagName) => {
        setActiveTagFilters(prev =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    // Filtreleri temizle
    const clearAllFilters = () => {
        setActivePlatformFilters([]);
        setActiveTagFilters([]);
    };

    // Otomasyon hangi platformlara sahip
    const getSchedulePlatforms = (schedule) => {
        const platforms = [];
        if (schedule.channel_ids) platforms.push('telegram');
        const ytId = schedule.youtube_channel_id || schedule.getlate_youtube_id;
        if (ytId) platforms.push('youtube');
        if (schedule.x_account_ids) {
            try {
                const ids = typeof schedule.x_account_ids === 'string' ? JSON.parse(schedule.x_account_ids) : schedule.x_account_ids;
                if (Array.isArray(ids) && ids.length > 0) platforms.push('x');
            } catch (e) { }
        }
        if (schedule.tiktok_account_ids) {
            try {
                const ids = typeof schedule.tiktok_account_ids === 'string' ? JSON.parse(schedule.tiktok_account_ids) : schedule.tiktok_account_ids;
                if (Array.isArray(ids) && ids.length > 0) platforms.push('tiktok');
            } catch (e) { }
        }
        if (schedule.enable_instagram_upload && schedule.instagram_account_ids) {
            try {
                const ids = typeof schedule.instagram_account_ids === 'string' ? JSON.parse(schedule.instagram_account_ids) : schedule.instagram_account_ids;
                if (Array.isArray(ids) && ids.length > 0) platforms.push('instagram');
            } catch (e) { }
        }
        return platforms;
    };

    // Otomasyonun etiketleri
    const getScheduleTagNames = (schedule) => {
        if (!schedule.tags || typeof schedule.tags !== 'string') return [];
        return schedule.tags.split(',').map(t => t.trim()).filter(Boolean);
    };

    // Filtrelenmiş otomasyonlar
    const filteredSchedules = schedules.filter(schedule => {
        // Platform filtresi
        if (activePlatformFilters.length > 0) {
            const schedulePlatforms = getSchedulePlatforms(schedule);
            const hasMatch = activePlatformFilters.some(pf => schedulePlatforms.includes(pf));
            if (!hasMatch) return false;
        }
        // Tag filtresi
        if (activeTagFilters.length > 0) {
            const scheduleTagNames = getScheduleTagNames(schedule);
            const hasMatch = activeTagFilters.some(tf => scheduleTagNames.includes(tf));
            if (!hasMatch) return false;
        }
        return true;
    });

    const hasActiveFilters = activePlatformFilters.length > 0 || activeTagFilters.length > 0;

    const formatDate = (dateString) => {
        if (!dateString) return 'Bilinmiyor';

        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffTime / (1000 * 60));

        if (diffDays === 0) {
            if (diffHours === 0) {
                if (diffMinutes < 5) return 'Az önce';
                return `${diffMinutes} dk önce`;
            }
            return `${diffHours} saat önce`;
        }

        if (diffDays === 1) {
            return 'Dün ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        }

        if (diffDays < 7) {
            return `${diffDays} gün önce`;
        }

        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getNextRunTime = (schedule) => {
        if (schedule.is_active !== 1) return null;

        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        if (schedule.schedule_mode === 'monthly' && schedule.monthly_schedules) {
            try {
                const schedules = JSON.parse(schedule.monthly_schedules);
                const currentDay = now.getDate();

                for (const ms of schedules) {
                    if (ms.day > currentDay ||
                        (ms.day === currentDay && (ms.hour > currentHour ||
                            (ms.hour === currentHour && ms.minute > currentMinute)))) {
                        return `${ms.day}. gün ${ms.hour.toString().padStart(2, '0')}:${ms.minute.toString().padStart(2, '0')}`;
                    }
                }
                if (schedules.length > 0) {
                    const first = schedules[0];
                    return `${first.day}. gün ${first.hour.toString().padStart(2, '0')}:${first.minute.toString().padStart(2, '0')}`;
                }
            } catch (e) {
                return null;
            }
        }

        if (schedule.daily_times) {
            try {
                const times = JSON.parse(schedule.daily_times);

                for (const time of times) {
                    if (time.hour > currentHour ||
                        (time.hour === currentHour && time.minute > currentMinute)) {
                        return `Bugün ${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}`;
                    }
                }

                if (times.length > 0) {
                    const first = times[0];
                    return `Yarın ${first.hour.toString().padStart(2, '0')}:${first.minute.toString().padStart(2, '0')}`;
                }
            } catch (e) {
                return null;
            }
        }

        return null;
    };

    const getPlatformData = (schedule) => {
        let telegramChannels = [];
        if (schedule.channel_ids) {
            const channelIdArray = schedule.channel_ids.split(',').map(id => parseInt(id));
            telegramChannels = channels.filter(c => channelIdArray.includes(c.id));
        }

        let youtubeChannel = null;
        const ytId = schedule.youtube_channel_id || schedule.getlate_youtube_id;

        if (ytId) {
            const cleanSearchId = String(ytId).replace('getlate_', '');

            youtubeChannel = youtubeChannels.find(ch => {
                const chId = String(ch.id || '');
                const chChannelId = String(ch.channel_id || '');
                const cleanChId = chId.replace('getlate_', '');
                const cleanChChannelId = chChannelId.replace('getlate_', '');

                return cleanSearchId === cleanChId ||
                    cleanSearchId === cleanChChannelId ||
                    cleanSearchId === chId ||
                    cleanSearchId === chChannelId;
            });
        }

        let xAccountsList = [];
        if (schedule.x_account_ids) {
            try {
                const ids = typeof schedule.x_account_ids === 'string'
                    ? JSON.parse(schedule.x_account_ids)
                    : schedule.x_account_ids;
                if (Array.isArray(ids)) {
                    xAccountsList = xAccounts.filter(acc => ids.includes(acc.id));
                }
            } catch (e) {
                xAccountsList = [];
            }
        }

        let tiktokAccountsList = [];
        if (schedule.tiktok_account_ids) {
            try {
                const ids = typeof schedule.tiktok_account_ids === 'string'
                    ? JSON.parse(schedule.tiktok_account_ids)
                    : schedule.tiktok_account_ids;

                if (Array.isArray(ids) && ids.length > 0) {
                    tiktokAccountsList = tiktokAccounts.filter(acc =>
                        ids.includes(acc._id) || ids.includes(acc.id)
                    );
                }
            } catch (e) {
                tiktokAccountsList = [];
            }
        }

        let instagramAccountsList = [];
        if (schedule.enable_instagram_upload && schedule.instagram_account_ids) {
            try {
                const ids = typeof schedule.instagram_account_ids === 'string'
                    ? JSON.parse(schedule.instagram_account_ids)
                    : schedule.instagram_account_ids;
                if (Array.isArray(ids)) {
                    instagramAccountsList = instagramAccounts.filter(acc => ids.includes(acc._id));
                }
            } catch (e) {
                instagramAccountsList = [];
            }
        }

        return {
            telegramChannels,
            telegramCount: telegramChannels.length,
            youtubeChannel,
            youtubeCount: youtubeChannel ? 1 : 0,
            xAccountsList,
            xCount: xAccountsList.length,
            tiktokAccountsList,
            tiktokCount: tiktokAccountsList.length,
            instagramAccountsList,
            instagramCount: instagramAccountsList.length
        };
    };

    const getFrequencyText = (schedule) => {
        if (schedule.schedule_mode === 'monthly') {
            try {
                const schedules = JSON.parse(schedule.monthly_schedules || '[]');
                return `Ayda ${schedules.length} kez`;
            } catch (e) {
                return 'Aylık';
            }
        }

        if (schedule.daily_times) {
            try {
                const times = JSON.parse(schedule.daily_times);
                return `Günde ${times.length} kez`;
            } catch (e) {
                return 'Günlük';
            }
        }

        return 'Günlük';
    };

    const getFrequencyLabel = (schedule) => {
        if (schedule.daily_schedules) {
            try {
                const schedules = JSON.parse(schedule.daily_schedules);
                return `${schedules.length} Özel Zaman`;
            } catch (e) { }
        }

        const frequencyOptions = [
            { value: 'once', label: 'Tek Seferlik' },
            { value: 'every_30_min', label: 'Her 30 Dakika' },
            { value: 'hourly', label: 'Saatlik' },
            { value: 'every_2_hours', label: '2 Saatte Bir' },
            { value: 'every_3_hours', label: '3 Saatte Bir' },
            { value: 'every_6_hours', label: '6 Saatte Bir' },
            { value: 'daily', label: 'Günlük' },
            { value: 'weekly', label: 'Haftalık' },
            { value: 'custom', label: 'Özel Zamanlama' }
        ];

        const option = frequencyOptions.find(opt => opt.value === schedule.frequency);
        return option ? option.label : schedule.frequency;
    };

    // Otomasyonlarda kullanılan etiketleri topla
    const usedTags = [...new Set(
        schedules.flatMap(s => getScheduleTagNames(s))
    )].map(name => tags.find(t => t.name === name)).filter(Boolean);

    return (
        <div className="automation-tab">
            <div className="automation-header">
                <button
                    className="btn-notification-settings"
                    onClick={() => setShowNotificationSettings(!showNotificationSettings)}
                >
                    Bildirimler
                </button>
                <button
                    className="btn-new-automation"
                    onClick={() => openModal()}
                >
                    + Yeni Otomasyon
                </button>
            </div>

            {/* Filtre Barı */}
            {schedules.length > 0 && (
                <div className="automation-filter-bar">
                    <div className="filter-platforms">
                        <button
                            className={`filter-platform-btn telegram ${activePlatformFilters.includes('telegram') ? 'active' : ''}`}
                            onClick={() => togglePlatformFilter('telegram')}
                            title="Telegram"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                        </button>
                        <button
                            className={`filter-platform-btn youtube ${activePlatformFilters.includes('youtube') ? 'active' : ''}`}
                            onClick={() => togglePlatformFilter('youtube')}
                            title="YouTube"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                        </button>
                        <button
                            className={`filter-platform-btn x-twitter ${activePlatformFilters.includes('x') ? 'active' : ''}`}
                            onClick={() => togglePlatformFilter('x')}
                            title="X"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                        </button>
                        <button
                            className={`filter-platform-btn tiktok ${activePlatformFilters.includes('tiktok') ? 'active' : ''}`}
                            onClick={() => togglePlatformFilter('tiktok')}
                            title="TikTok"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                            </svg>
                        </button>
                        <button
                            className={`filter-platform-btn instagram ${activePlatformFilters.includes('instagram') ? 'active' : ''}`}
                            onClick={() => togglePlatformFilter('instagram')}
                            title="Instagram"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                            </svg>
                        </button>
                    </div>

                    <div className="filter-separator"></div>

                    <div className="tag-filter-wrapper">
                        <button
                            className={`filter-tag-btn ${activeTagFilters.length > 0 ? 'active' : ''}`}
                            onClick={() => setShowTagFilterDropdown(!showTagFilterDropdown)}
                        >
                            Etiketler
                            {activeTagFilters.length > 0 && (
                                <span className="filter-tag-count">{activeTagFilters.length}</span>
                            )}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>

                        {showTagFilterDropdown && (
                            <div className="tag-filter-dropdown">
                                {usedTags.length > 0 ? (
                                    usedTags.map(tag => (
                                        <label key={tag.id} className="tag-filter-item">
                                            <input
                                                type="checkbox"
                                                checked={activeTagFilters.includes(tag.name)}
                                                onChange={() => toggleTagFilter(tag.name)}
                                            />
                                            <span
                                                className="tag-filter-color"
                                                style={{ backgroundColor: tag.color || '#667eea' }}
                                            ></span>
                                            <span className="tag-filter-name">{tag.name}</span>
                                        </label>
                                    ))
                                ) : (
                                    <div className="tag-filter-empty">Etiket bulunamadi</div>
                                )}
                            </div>
                        )}
                    </div>

                    {hasActiveFilters && (
                        <button className="filter-clear-btn" onClick={clearAllFilters}>
                            Temizle
                        </button>
                    )}

                    {hasActiveFilters && (
                        <span className="filter-result-count">
                            {filteredSchedules.length}/{schedules.length}
                        </span>
                    )}
                </div>
            )}

            {showNotificationSettings && (
                <div className="notification-settings-panel">
                    <div className="notification-settings-header">
                        <h4>Bildirim Ayarlari</h4>
                        <button className="notification-close-btn" onClick={() => setShowNotificationSettings(false)}>
                            ✕
                        </button>
                    </div>
                    <div className="notification-settings-body">
                        <div className="notification-field">
                            <label>Bildirim Hedefi</label>
                            <div className="notification-target-info">
                                Telegram dogrulamasi yapilmis tum kullanicilara gonderilir
                            </div>
                        </div>
                        <div className="notification-toggles">
                            <label className="notification-toggle">
                                <input
                                    type="checkbox"
                                    checked={notificationSettings.notify_on_last_content}
                                    onChange={(e) => setNotificationSettings(prev => ({
                                        ...prev,
                                        notify_on_last_content: e.target.checked
                                    }))}
                                />
                                <span>Son 1 icerik kaldiginda uyar</span>
                            </label>
                            <label className="notification-toggle">
                                <input
                                    type="checkbox"
                                    checked={notificationSettings.notify_on_empty}
                                    onChange={(e) => setNotificationSettings(prev => ({
                                        ...prev,
                                        notify_on_empty: e.target.checked
                                    }))}
                                />
                                <span>Icerik tukendiginde uyar</span>
                            </label>
                        </div>
                    </div>
                    <div className="notification-settings-footer">
                        <button
                            className="btn-test-notification"
                            onClick={sendTestNotification}
                        >
                            Test Gonder
                        </button>
                        <button
                            className="btn-save-notification"
                            onClick={saveNotificationSettings}
                            disabled={notificationSaving}
                        >
                            {notificationSaving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                </div>
            )}

            {schedules.length === 0 ? (
                <div className="empty-state">
                    <h3>Henüz otomasyon kuralı yok</h3>
                    <p>İlk otomasyon kuralınızı oluşturarak içeriklerinizi<br />otomatik olarak paylaşmaya başlayın</p>
                    <button
                        className="btn-create-first"
                        onClick={() => openModal()}
                    >
                        İlk Otomasyonu Oluştur
                    </button>
                </div>
            ) : filteredSchedules.length === 0 && hasActiveFilters ? (
                <div className="empty-state">
                    <h3>Filtre sonucu bulunamadi</h3>
                    <p>Secili filtrelere uygun otomasyon yok</p>
                    <button
                        className="btn-create-first"
                        onClick={clearAllFilters}
                    >
                        Filtreleri Temizle
                    </button>
                </div>
            ) : (
                <div className="schedules-grid">
                    {filteredSchedules.map(schedule => {
                        const isActive = schedule.is_active === 1;
                        const platformData = getPlatformData(schedule);
                        const nextRun = getNextRunTime(schedule);

                        let selectedTags = [];
                        if (schedule.tags) {
                            if (typeof schedule.tags === 'string' && schedule.tags.length > 0) {
                                const tagNames = schedule.tags.split(',').map(name => name.trim());
                                selectedTags = tagNames.map(name => {
                                    return tags.find(t => t.name === name);
                                }).filter(tag => tag !== undefined);
                            }
                        }

                        let dailyTimes = [];
                        if (schedule.daily_times) {
                            try {
                                dailyTimes = JSON.parse(schedule.daily_times);
                            } catch (e) {
                                dailyTimes = [];
                            }
                        }

                        let monthlySchedules = [];
                        if (schedule.monthly_schedules) {
                            try {
                                monthlySchedules = JSON.parse(schedule.monthly_schedules);
                            } catch (e) {
                                monthlySchedules = [];
                            }
                        }

                        const now = new Date();
                        const currentHour = now.getHours();
                        const currentMinute = now.getMinutes();
                        const currentDay = now.getDate();

                        return (
                            <div
                                key={schedule.id}
                                className={`schedule-card ${isActive ? 'active-card' : 'inactive-card'}`}
                            >
                                <div className={`schedule-status-bar ${isActive ? '' : 'inactive'}`}>
                                    <div className="status-indicator">
                                        <span className="status-dot"></span>
                                        <span className="status-text">
                                            {isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </div>
                                    {isActive && nextRun && (
                                        <div className="next-run-time">
                                            <span>⏰</span>
                                            <span>{nextRun}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="schedule-card-content">
                                    <div className="schedule-header">
                                        <div className="schedule-header-left">
                                            <h3>
                                                {(() => {
                                                    if (schedule.channel_ids) {
                                                        const channelIdArray = schedule.channel_ids.split(',').map(id => parseInt(id));
                                                        const selectedChannels = channels.filter(c => channelIdArray.includes(c.id));

                                                        if (selectedChannels.length > 0) {
                                                            if (selectedChannels.length === 1) {
                                                                return selectedChannels[0].name;
                                                            } else {
                                                                return `${selectedChannels[0].name} +${selectedChannels.length - 1}`;
                                                            }
                                                        }
                                                    }
                                                    return 'Otomasyon';
                                                })()}
                                            </h3>
                                            <div className="schedule-info-badges">
                                                <span className={`schedule-mode-indicator ${schedule.schedule_mode || 'daily'}`}>
                                                    {schedule.schedule_mode === 'monthly' ? 'Aylık' : 'Günlük'}
                                                </span>
                                                <span className="schedule-frequency">
                                                    {getFrequencyText(schedule)}
                                                </span>
                                            </div>
                                        </div>
                                        <label className="switch" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={() => toggleSchedule(schedule.id, isActive)}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                    </div>

                                    <div className="platforms-section">
                                        <div className="section-title">
                                            Platformlar
                                        </div>
                                        <div className="platforms-grid">
                                            {platformData.telegramCount > 0 && (
                                                <div className="platform-badge telegram">
                                                    <span className="platform-name">Telegram</span>
                                                    <span className="platform-count">{platformData.telegramCount}</span>
                                                    <div className="platform-tooltip">
                                                        <div className="platform-tooltip-title">
                                                            Telegram Kanalları
                                                        </div>
                                                        <div className="platform-tooltip-list">
                                                            {platformData.telegramChannels.map(ch => (
                                                                <div key={ch.id} className="platform-tooltip-item">
                                                                    {ch.profile_photo ? (
                                                                        <img
                                                                            src={`${API_URL.replace('/api', '')}/uploads/${ch.profile_photo}`}
                                                                            alt={ch.name}
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <span className="tooltip-avatar">
                                                                            {ch.name?.charAt(0).toUpperCase() || 'T'}
                                                                        </span>
                                                                    )}
                                                                    <span>{ch.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {platformData.youtubeCount > 0 && (
                                                <div className="platform-badge youtube">
                                                    <span className="platform-name">YouTube</span>
                                                    <span className="platform-count">{platformData.youtubeCount}</span>
                                                    <div className="platform-tooltip">
                                                        <div className="platform-tooltip-title">
                                                            YouTube Kanalı
                                                        </div>
                                                        <div className="platform-tooltip-list">
                                                            <div className="platform-tooltip-item">
                                                                {(platformData.youtubeChannel.channel_thumbnail || platformData.youtubeChannel.thumbnail || platformData.youtubeChannel.profilePicture) ? (
                                                                    <img
                                                                        src={platformData.youtubeChannel.channel_thumbnail || platformData.youtubeChannel.thumbnail || platformData.youtubeChannel.profilePicture}
                                                                        alt={platformData.youtubeChannel.channel_name || platformData.youtubeChannel.name}
                                                                    />
                                                                ) : (
                                                                    <span className="tooltip-avatar">
                                                                        {(platformData.youtubeChannel.channel_name || platformData.youtubeChannel.name)?.charAt(0).toUpperCase() || 'Y'}
                                                                    </span>
                                                                )}
                                                                <span>{platformData.youtubeChannel.channel_name || platformData.youtubeChannel.name}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {platformData.xCount > 0 && (
                                                <div className="platform-badge x-twitter">
                                                    <span className="platform-name">X</span>
                                                    <span className="platform-count">{platformData.xCount}</span>
                                                    <div className="platform-tooltip">
                                                        <div className="platform-tooltip-title">
                                                            X Hesapları
                                                        </div>
                                                        <div className="platform-tooltip-list">
                                                            {platformData.xAccountsList.map(acc => (
                                                                <div key={acc.id} className="platform-tooltip-item">
                                                                    {acc.profile_image_url ? (
                                                                        <img
                                                                            src={acc.profile_image_url}
                                                                            alt={acc.username}
                                                                        />
                                                                    ) : (
                                                                        <span className="tooltip-avatar">
                                                                            {acc.username?.charAt(0).toUpperCase() || 'X'}
                                                                        </span>
                                                                    )}
                                                                    <span>@{acc.username}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {platformData.tiktokCount > 0 && (
                                                <div className="platform-badge tiktok">
                                                    <span className="platform-name">TikTok</span>
                                                    <span className="platform-count">{platformData.tiktokCount}</span>
                                                    <div className="platform-tooltip">
                                                        <div className="platform-tooltip-title">
                                                            TikTok Hesapları
                                                        </div>
                                                        <div className="platform-tooltip-list">
                                                            {platformData.tiktokAccountsList.map(acc => (
                                                                <div key={acc._id} className="platform-tooltip-item">
                                                                    {acc.profilePicture ? (
                                                                        <img
                                                                            src={acc.profilePicture}
                                                                            alt={acc.username || acc.displayName}
                                                                        />
                                                                    ) : (
                                                                        <span className="tooltip-avatar">
                                                                            {(acc.username || acc.displayName)?.charAt(0).toUpperCase() || 'T'}
                                                                        </span>
                                                                    )}
                                                                    <span>@{acc.username || acc.displayName}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {platformData.instagramCount > 0 && (
                                                <div className="platform-badge instagram">
                                                    <span className="platform-name">Instagram</span>
                                                    <span className="platform-count">{platformData.instagramCount}</span>
                                                    <div className="platform-tooltip">
                                                        <div className="platform-tooltip-title">
                                                            Instagram Hesapları
                                                        </div>
                                                        <div className="platform-tooltip-list">
                                                            {platformData.instagramAccountsList.map(acc => (
                                                                <div key={acc._id} className="platform-tooltip-item">
                                                                    {acc.profilePicture ? (
                                                                        <img
                                                                            src={acc.profilePicture}
                                                                            alt={acc.username}
                                                                        />
                                                                    ) : (
                                                                        <span className="tooltip-avatar">
                                                                            {acc.username?.charAt(0).toUpperCase() || 'I'}
                                                                        </span>
                                                                    )}
                                                                    <span>@{acc.username}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {platformData.telegramCount === 0 &&
                                                platformData.youtubeCount === 0 &&
                                                platformData.xCount === 0 &&
                                                platformData.tiktokCount === 0 &&
                                                platformData.instagramCount === 0 && (
                                                    <span className="no-platforms">Platform seçilmemiş</span>
                                                )}
                                        </div>
                                    </div>

                                    <div className="tags-section">
                                        <div className="section-title">
                                            İçerik Etiketleri
                                        </div>
                                        <div className="tags-display">
                                            {selectedTags.length > 0 ? (
                                                selectedTags.map(tag => {
                                                    const tagStat = schedule.tag_stats?.find(
                                                        ts => ts.name.toLowerCase() === tag.name.toLowerCase()
                                                    );

                                                    let countClass = '';
                                                    if (tagStat) {
                                                        if (tagStat.remaining === 0) {
                                                            countClass = 'empty';
                                                        } else if (tagStat.remaining <= 3) {
                                                            countClass = 'low';
                                                        }
                                                    }

                                                    return (
                                                        <span
                                                            key={tag.id}
                                                            className="tag-badge"
                                                            style={{ backgroundColor: tag.color || '#667eea' }}
                                                            title={tagStat ? `${tagStat.used} kullanıldı, ${tagStat.remaining} kaldı` : ''}
                                                        >
                                                            {tag.name}
                                                            {tagStat && (
                                                                <span className={`automation-tag-count ${countClass}`}>
                                                                    {tagStat.remaining}/{tagStat.total}
                                                                </span>
                                                            )}
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span className="no-tags">Etiket seçilmemiş</span>
                                            )}
                                        </div>
                                    </div>

                                    {schedule.schedule_mode !== 'monthly' && dailyTimes.length > 0 && (
                                        <div className="times-section">
                                            <div className="section-title">
                                                Paylaşım Saatleri
                                            </div>
                                            <div className="schedule-times-preview">
                                                {dailyTimes.map((time, index) => {
                                                    const isNextTime = isActive && (
                                                        time.hour > currentHour ||
                                                        (time.hour === currentHour && time.minute > currentMinute)
                                                    ) && !dailyTimes.slice(0, index).some(t =>
                                                        t.hour > currentHour ||
                                                        (t.hour === currentHour && t.minute > currentMinute)
                                                    );

                                                    return (
                                                        <span
                                                            key={index}
                                                            className={`time-badge ${isNextTime ? 'next-time' : ''}`}
                                                        >
                                                            {time.hour.toString().padStart(2, '0')}:{time.minute.toString().padStart(2, '0')}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {schedule.schedule_mode === 'monthly' && monthlySchedules.length > 0 && (
                                        <div className="times-section">
                                            <div className="section-title">
                                                Aylık Zamanlamalar
                                            </div>
                                            <div className="monthly-schedules-list">
                                                {monthlySchedules.map((ms, index) => {
                                                    const isToday = ms.day === currentDay;
                                                    const isPast = ms.day < currentDay ||
                                                        (isToday && (ms.hour < currentHour ||
                                                            (ms.hour === currentHour && ms.minute <= currentMinute)));

                                                    return (
                                                        <div
                                                            key={index}
                                                            className={`monthly-schedule-item ${isToday && !isPast ? 'is-today' : ''} ${isPast ? 'is-past' : ''}`}
                                                        >
                                                            <span className="monthly-day-text">
                                                                {ms.day === 'last' ? 'Ayın son günü' : `Ayın ${ms.day}. günü`}
                                                            </span>
                                                            <span className="monthly-time-badge">
                                                                {ms.hour.toString().padStart(2, '0')}:{ms.minute.toString().padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    <div className="card-footer">
                                        <div className="footer-left">
                                            <span className="footer-username">
                                                {schedule.created_by_username || schedule.created_by || 'Sistem'}
                                            </span>
                                            <span className="footer-date">
                                                {formatDate(schedule.created_at || schedule.updated_at)}
                                            </span>
                                        </div>

                                        <div className="footer-actions">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openHistoryModal(schedule);
                                                }}
                                                className="icon-btn"
                                                title="Geçmiş"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                            </button>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openModal(schedule);
                                                }}
                                                className="icon-btn"
                                                title="Düzenle"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <AutomationModal
                isOpen={showScheduleModal}
                onClose={closeModal}
                editingSchedule={editingSchedule}
                channels={channels}
                tags={tags}
                onSave={handleModalSave}
                onDelete={handleModalDelete}
            />

            <AutomationHistoryModal
                isOpen={showHistoryModal}
                onClose={closeHistoryModal}
                automationId={selectedAutomationForHistory?.id}
                automationName={(() => {
                    if (!selectedAutomationForHistory) return '';
                    const channel = channels.find(c => c.id === selectedAutomationForHistory.channel_id);
                    const channelName = channel?.name || 'Kanal';
                    return `${channelName} - ${getFrequencyLabel(selectedAutomationForHistory)}`;
                })()}
                automationData={selectedAutomationForHistory}
            />
        </div>
    );
}

export default AutomationTab;